import { Fragment, useMemo } from 'react';

import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { CityLinks as ICityLinks } from '@/types/generated';
import TAGLink from '@/components/TAGLink';
import { useAppContext, useOfficeLocationContext } from '@/context';
import { TCity } from '@/types';
import {
	clickContext,
	joinValidStrs,
	officeURLByBrand,
	stateStrByBrand,
	strToSlug,
	useTemplateContainerFullWidthStyles,
} from '@/utils';
import TemplateContainer from '@/components/TemplateContainer';

import { linkItemWrapper } from '../Centers.styles';

export default function CityLinks({ sectionTitle, useDisplayName, groupByState }: ICityLinks) {
	const theme = useTheme();
	const { config } = useAppContext();
	const isMediumDevice = useMediaQuery(theme.breakpoints.between('md', 'lg'));

	// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	const { cities } = useOfficeLocationContext() as { cities: TCity[] };

	const citiesByStateMap: Map<string, TCity[]> = useMemo(() => {
		const map = new Map<string, TCity[]>();

		cities.sort((aCity: TCity, bCity: TCity) => {
			const cityAStr = aCity.city || aCity.location?.city || '';
			const cityBStr = bCity.city || bCity.location?.city || '';

			const linkAText = config.themeId === 'wellnow' && aCity.displayName ? aCity.displayName : cityAStr;
			const linkBText = config.themeId === 'wellnow' && bCity.displayName ? bCity.displayName : cityBStr;

			return linkAText.localeCompare(linkBText);
		});

		cities.forEach((city) => {
			const state = city.state;
			if (map.has(state)) {
				const existingCities = map.get(state) as TCity[];
				map.set(state, [...existingCities, city]);
			} else {
				map.set(state, [city]);
			}
		});

		const sortedMap = new Map(
			Array.from(map).sort(([keyA], [keyB]) => {
				return keyA.localeCompare(keyB);
			})
		);

		return sortedMap;
	}, [cities, config.themeId]);

	return (
		<TemplateContainer
			disableGutters={false}
			maxWidth={false}
			dataTestId="section_city_links_wrapper"
			containerSx={useTemplateContainerFullWidthStyles(['Top', 'Bottom'], isMediumDevice)}
		>
			<Container data-testid="city_links_section">
				{sectionTitle && (
					<Typography
						variant="header4"
						color="text.secondary"
						my={1.5}
						data-testid={'city_links_section_title'}
					>
						{sectionTitle}
					</Typography>
				)}
				{cities.length ? (
					<>
						{groupByState &&
							Array.from(citiesByStateMap.keys()).map((state, i) => {
								return (
									<Fragment key={state}>
										<Typography
											sx={{ margin: i !== 0 ? '10rem 0 0.10rem 0' : '' }}
											variant="header4"
											color="text.secondary"
											data-testid="city_links_state_title"
										>
											{state}
										</Typography>
										<Grid
											container
											columns={{ xs: 1, sm: 4 }}
											justifyContent="flex-start"
											data-testid="city_links_box_wrapper"
										>
											{citiesByStateMap.get(state)?.map((city, index) => {
												return (
													<CityLink
														key={city.city + city.state}
														city={city}
														index={index}
														useDisplayName={useDisplayName}
														groupByState
													/>
												);
											})}
										</Grid>
									</Fragment>
								);
							})}

						{!groupByState && (
							<Grid
								container
								spacing={2}
								columns={{ xs: 1, sm: 4 }}
								justifyContent="flex-start"
								data-testid="city_links_box_wrapper"
							>
								{cities
									.sort((aCity: TCity, bCity: TCity) => {
										const cityAStr = aCity.city || aCity.location?.city || '';
										const cityBStr = bCity.city || bCity.location?.city || '';

										const linkAText =
											config.themeId === 'wellnow' && aCity.displayName
												? aCity.displayName
												: cityAStr;
										const linkBText =
											config.themeId === 'wellnow' && bCity.displayName
												? bCity.displayName
												: cityBStr;

										return linkAText.localeCompare(linkBText);
									})
									.map((city: TCity, index: number) => {
										return (
											<CityLink
												city={city}
												index={index}
												useDisplayName={useDisplayName}
												key={city.city + city.state}
											/>
										);
									})}
							</Grid>
						)}
					</>
				) : null}
			</Container>
		</TemplateContainer>
	);
}

type CityLinkProps = {
	index: number;
	city: TCity;
	useDisplayName: boolean | null | undefined;
	groupByState?: boolean | null | undefined;
};

const CityLink = ({ index, city, useDisplayName, groupByState }: CityLinkProps) => {
	const { environment, config } = useAppContext();
	const facilityBasePath = config.features.otherApps.facilityDetailsBasePath;

	const stateStr = stateStrByBrand(config.themeId, city);
	const cityStr = city.city || city.location?.city || undefined;

	const center = city.centers?.[0];
	const addressStr = center?.address?.address1 || center?.location?.street1 || city.location?.street1 || undefined;
	const zipStr =
		center?.address?.zipCode || center?.location?.zip || city.location?.zip || city.location?.zipCode || undefined;

	function computeLinkText(): string {
		if (useDisplayName && city.displayName) {
			return city.displayName;
		} else if (groupByState) {
			return cityStr || '';
		} else {
			return joinValidStrs(', ', [cityStr, stateStr]);
		}
	}

	const href = `/${facilityBasePath}/${officeURLByBrand(config.themeId, {
		stateStr,
		city: city?.centers && city?.centers?.length > 1 ? undefined : cityStr,
		address: addressStr?.replace(/\//g, '-'),
		zip: zipStr,
	})}/`;

	const dataId = `city_links_link_item_${strToSlug(
		joinValidStrs('_', [stateStr, cityStr, city?.location?.street1])
	)}`;

	const linkText = computeLinkText();

	// need to reload the page to go to the new ODP app
	// based on the feature flag when brand is ready with new ODP app
	const siteUrl = environment === 'prod' ? config.siteURL : config.siteURLStage;
	const updatedHref = addressStr && config.featureFlags?.enablePageReloadOnOdpLink ? siteUrl + href : href;
	return (
		<Grid item xs={1} sm={1} key={`city_links_link_${index}`}>
			<Box data-testid={dataId} sx={linkItemWrapper}>
				<TAGLink
					href={updatedHref}
					analyticsContext={clickContext({
						brand: config.name,
						text: linkText,
					})}
				>
					<Typography variant="bodyMediumBook" color="text.interactive" sx={{ textDecoration: 'underline' }}>
						{linkText}
					</Typography>
				</TAGLink>
			</Box>
		</Grid>
	);
};
